/**
 * stripeJs.ts: the website's Stripe.js loader, inert without a key (card
 * 1218526698505993, "Web checkout S4"; docs/web-checkout-lane-s3-s6.md §4).
 *
 * `loadStripeJs()` loads https://js.stripe.com/v3/ through the install's own
 * `loadScript()` (WebInstall.tsx, handed in as `load` so this file never
 * imports the install and the install can import this), then returns
 * `window.Stripe(publishableKey)`, or null. Null, and never a throw, when:
 *
 *   · there is no publishable key in this build (and then NOTHING loads: no
 *     script tag, no request to Stripe, no Stripe cookies);
 *   · the script fails to load (network, or the CSP refusing it);
 *   · `window.Stripe` is not a function after the load (a script that loaded
 *     but did not run still fires `load`, see loadScript's own note);
 *   · `Stripe(key)` itself throws.
 *
 * Each null says why in the console, once, so a dead payment element is never
 * silent for whoever opens DevTools. The screen side of that is Blake's: a
 * payer's button stays disabled and says card payment isn't connected.
 *
 * WHY NO npm PACKAGE. Stripe.js must be loaded from js.stripe.com (it cannot
 * be bundled), so @stripe/stripe-js would only be a loader and types. We own
 * the loader, the few types below are all the fee screen uses, and there is no
 * lockfile change.
 *
 * The key comes from the build (Tyler's ruling N1: VITE_STRIPE_PUBLISHABLE_KEY
 * through vite.config.ts's define block) and is checked again here with the
 * same rule the build used (src/lib/stripePublishableKey.ts): a key that is
 * not pk_test_ or pk_live_ is treated as no key at all.
 */

import { publishableKeyOrNull } from "../lib/stripePublishableKey";

/** Stripe's evergreen v3 URL. Same host as the CSP's script-src entry. */
export const STRIPE_JS_SRC = "https://js.stripe.com/v3/";

/* ── the part of Stripe.js the fee screen uses ────────────────────────────── */

export interface StripeErrorLike {
  type?: string;
  code?: string;
  message?: string;
}

export interface StripeElementLike {
  mount(target: HTMLElement): void;
  on(event: string, handler: (event?: unknown) => void): void;
  destroy(): void;
  clear?(): void;
}

export interface StripeElementsLike {
  create(type: "payment", options?: Record<string, unknown>): StripeElementLike;
  submit(): Promise<{ error?: StripeErrorLike }>;
}

export interface StripeLike {
  elements(options: Record<string, unknown>): StripeElementsLike;
  confirmPayment(options: {
    elements: StripeElementsLike;
    clientSecret: string;
    confirmParams?: Record<string, unknown>;
    redirect: "if_required";
  }): Promise<{ error?: StripeErrorLike }>;
  /** IP4b (M8): read an intent back after an unknown outcome (inPersonPay.ts check()). Optional so a test double
   *  without it still type-checks; Stripe.js always has it. */
  retrievePaymentIntent?(clientSecret: string): Promise<{ paymentIntent?: { status: string }; error?: StripeErrorLike }>;
}

type StripeConstructor = (publishableKey: string) => StripeLike;

/* ── the key ──────────────────────────────────────────────────────────────── */

/** This build's publishable key, or null. The argument is for tests. */
export function getPublishableKey(
  raw: unknown = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
): string | null {
  return publishableKeyOrNull(raw);
}

/* ── the loader ───────────────────────────────────────────────────────────── */

export interface LoadStripeJsOptions {
  /** The install's loadScript. Injected so this module never imports WebInstall. */
  load: (src: string) => Promise<void>;
  /** Defaults to this build's key. Passing null means "no key". */
  key?: string | null;
  win?: Window;
}

/** One Stripe instance per key per page, the way Stripe asks for it. A failed
 *  attempt is forgotten so the next call can try again (loadScript forgets a
 *  failed load the same way). */
const instances = new Map<string, Promise<StripeLike | null>>();

let warnedNoKey = false;

export function loadStripeJs({ load, key, win = window }: LoadStripeJsOptions): Promise<StripeLike | null> {
  const pk = key === undefined ? getPublishableKey() : publishableKeyOrNull(key);
  if (!pk) {
    if (!warnedNoKey) {
      warnedNoKey = true;
      console.warn("[stripe-js] no publishable key in this build: Stripe.js is not loaded and card payment stays off");
    }
    return Promise.resolve(null);
  }
  const cached = instances.get(pk);
  if (cached) return cached;

  const attempt = (async (): Promise<StripeLike | null> => {
    try {
      await load(STRIPE_JS_SRC);
    } catch (err) {
      console.warn("[stripe-js] Stripe.js failed to load (network, or the page's CSP refused it):", err);
      return null;
    }
    const ctor = (win as Window & { Stripe?: unknown }).Stripe;
    if (typeof ctor !== "function") {
      console.warn("[stripe-js] Stripe.js loaded but window.Stripe is not a function; card payment stays off");
      return null;
    }
    try {
      return (ctor as StripeConstructor)(pk);
    } catch (err) {
      console.warn("[stripe-js] Stripe(publishableKey) threw; card payment stays off:", err);
      return null;
    }
  })();

  instances.set(pk, attempt);
  void attempt.then((stripe) => {
    if (!stripe && instances.get(pk) === attempt) instances.delete(pk);
  });
  return attempt;
}

/** Tests only: forget every instance and the one-time warning. */
export function forgetStripeJs(): void {
  instances.clear();
  warnedNoKey = false;
}
